import { useId, useState, type FocusEvent } from 'react';

export interface LwStateChip {
  code: string;
  label?: string;
}

export interface LwStateChipsProps {
  states: readonly LwStateChip[];
  visibleCount?: number;
  ariaLabel?: string;
}

export function LwStateChips({ states, visibleCount = 2, ariaLabel = 'States' }: LwStateChipsProps) {
  const [hovered, setHovered] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const disclosureId = useId();
  const visible = states.slice(0, visibleCount);
  const remaining = states.slice(visibleCount);
  const open = (hovered || focusWithin || pinned) && !dismissed;

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setFocusWithin(false);
      setDismissed(false);
    }
  };

  return (
    <div className="lw-state-chips" role="group" aria-label={ariaLabel}>
      {visible.map((state) => <span key={state.code} title={state.label}>{state.code}</span>)}
      {remaining.length > 0 && (
        <div
          className="lw-state-chips__overflow"
          data-open={open || undefined}
          onMouseEnter={() => {
            setHovered(true);
            setDismissed(false);
          }}
          onMouseLeave={() => setHovered(false)}
          onFocus={() => {
            setFocusWithin(true);
            setDismissed(false);
          }}
          onBlur={handleBlur}
        >
          <button
            type="button"
            aria-expanded={open}
            aria-controls={disclosureId}
            aria-label={`Show ${remaining.length} more states`}
            onClick={() => {
              if (pinned) {
                setPinned(false);
                setDismissed(true);
                return;
              }
              setPinned(true);
              setDismissed(false);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape' && open) {
                event.preventDefault();
                event.stopPropagation();
                setPinned(false);
                setDismissed(true);
              }
            }}
          >
            +{remaining.length}
          </button>
          <div id={disclosureId} className="lw-state-chips__more" role="group" aria-label="Additional states">
            {states.map((state) => <span key={state.code}>{state.label ?? state.code}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}
